/**
 * ABTestPanel.tsx
 *
 * Side-by-side comparison of an A/B test's control vs variant run.
 * Shown above the step list in ObservabilityPage when the current run has an
 * ab_test_id. Data: GET /v1/runs/{runId}/ab-comparison.
 *
 * "Mark winner" reuses the per-step feedback endpoint
 * (POST /v1/runs/{runId}/steps/{stepId}/feedback, feedback: 1) for every step
 * in the chosen run — the same training-signal channel as the step sidebar.
 *
 * The control and variant runs execute with their respective models (the LLM
 * router resolves provider/model per run), so this is a live comparison.
 */

import { useCallback, useEffect, useState } from 'react'
import { Trophy } from 'lucide-react'

import { api } from '../api/client'
import { classifyStepError } from '../lib/classifyStepError'
import { GlassPanel } from './common/GlassPanel'
import { useWriteAccess } from '../lib/writeAccess'

// ── Types (mirror contracts.ABComparison JSON) ────────────────────────────────

interface ComparisonStep {
  id: string
  stepIndex: number
  stepType?: string
  status: string
  inputPayload?: Record<string, unknown>
  outputPayload?: Record<string, unknown>
  errorCode?: string
  errorMessage?: string
}

interface ComparisonSide {
  run_id: string
  model: string
  provider?: string
  status: string
  cost_micros: number
  latency_ms: number
  steps: ComparisonStep[]
}

interface ABComparison {
  ab_test_id: string
  control?: ComparisonSide
  variant?: ComparisonSide
}

type LoadState = 'loading' | 'ready' | 'error'

// ── Formatting ────────────────────────────────────────────────────────────────

function fmtCost(micros: number): string {
  return `£${(micros / 1_000_000).toFixed(4)}`
}
function fmtLatency(ms: number): string {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

// ── Column ──────────────────────────────────────────────────────────────────

function Column({
  label,
  accent,
  side,
  steps,
  marking,
  onMarkWinner,
}: {
  label: string
  accent: string
  side: ComparisonSide | undefined
  steps: ComparisonStep[]
  marking: boolean
  /** Absent for a role without platform access: no Mark winner button. */
  onMarkWinner?: () => void
}) {
  return (
    <GlassPanel className="flex-1 min-w-0 p-4 flex flex-col gap-3" style={{ borderColor: `${accent}40` }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[9.5px] font-[800] tracking-[0.12em] uppercase" style={{ color: accent }}>
            {label}
          </span>
          <div className="text-[14px] font-[800] text-[#dce9f7] truncate">{side?.model || '—'}</div>
          {side?.provider && <div className="text-[10.5px] text-[#4e6480] font-mono">{side.provider}</div>}
        </div>
        {onMarkWinner && <button
          type="button"
          disabled={!side || marking}
          onClick={onMarkWinner}
          className="inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-[8px] border text-[11px] font-bold cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          style={{ borderColor: `${accent}55`, color: accent, background: `${accent}14` }}
          title="Record positive feedback on all steps of this run"
        >
          <Trophy size={13} /> {marking ? 'Saving…' : 'Mark winner'}
        </button>}
      </div>

      {/* Perf strip */}
      <div className="flex items-center gap-5 text-[11px] border-y border-[rgba(148,163,184,0.1)] py-2">
        <div className="flex flex-col">
          <span className="text-[9px] font-[800] tracking-[0.08em] uppercase text-[#3a5068]">Cost</span>
          <span className="text-[#c8d9ee] tabular-nums font-mono">{side ? fmtCost(side.cost_micros) : '—'}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[9px] font-[800] tracking-[0.08em] uppercase text-[#3a5068]">Latency</span>
          <span className="text-[#c8d9ee] tabular-nums font-mono">{side ? fmtLatency(side.latency_ms) : '—'}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[9px] font-[800] tracking-[0.08em] uppercase text-[#3a5068]">Status</span>
          <span className="text-[#8fa3bd]">{side?.status || '—'}</span>
        </div>
      </div>

      {/* Step outputs */}
      <div className="flex flex-col gap-2">
        {steps.length === 0 && <p className="text-[11px] text-[#3a5068] italic m-0">No steps.</p>}
        {steps.map((step, i) => {
          const failed = step.status === 'failed'
          const friendly = failed ? classifyStepError(step.errorCode, step.errorMessage) : null
          const rawError = [step.errorCode ? `[${step.errorCode}]` : '', step.errorMessage ?? ''].join(' ').trim()
          return (
          <div key={step.id || i} className="rounded-[9px] border border-[rgba(148,163,184,0.1)] bg-[rgba(6,10,16,0.55)] p-[10px]">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-[18px] h-[18px] rounded-full text-[10px] font-[800] flex items-center justify-center tabular-nums" style={{ background: `${accent}1c`, color: accent }}>
                {step.stepIndex + 1}
              </span>
              <span className="text-[10.5px] text-[#8fa3bd] font-mono truncate">{step.stepType || 'step'}</span>
              <span className="ml-auto text-[9.5px] text-[#4e6480]">{step.status}</span>
            </div>
            {failed && (friendly || rawError) ? (
              <div className="flex flex-col gap-1.5">
                {friendly && (
                  <div className="px-[10px] py-2 bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.28)] rounded-[8px] text-[10.5px] text-[#fbe2b3] leading-[1.5]">
                    {friendly}
                  </div>
                )}
                {rawError && (
                  <details className="group">
                    <summary className="cursor-pointer select-none text-[9.5px] font-semibold text-[#4e6480] hover:text-[#8fa3bd]">Technical details</summary>
                    <pre className="font-mono text-[10px] leading-[1.5] text-[#fca5a5] m-0 mt-1.5 whitespace-pre-wrap break-words">{rawError}</pre>
                  </details>
                )}
              </div>
            ) : (
              <pre className="font-mono text-[10.5px] leading-[1.5] text-[#8fa3bd] m-0 whitespace-pre-wrap break-words max-h-[160px] overflow-y-auto sds-body-scroll">
                {step.outputPayload && Object.keys(step.outputPayload).length > 0
                  ? JSON.stringify(step.outputPayload, null, 2)
                  : '—'}
              </pre>
            )}
          </div>
        )})}
      </div>
    </GlassPanel>
  )
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function ABTestPanel({ runId }: { runId: string }) {
  const [data, setData] = useState<ABComparison | null>(null)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [marking, setMarking] = useState<'control' | 'variant' | null>(null)
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  // Step feedback is a PLATFORM write (owner, admin, developer —
  // lib/writeAccess; fail closed).
  const { platform } = useWriteAccess()

  const load = useCallback(async () => {
    setLoadState('loading')
    try {
      const res = await api.get<ABComparison>(`/v1/runs/${encodeURIComponent(runId)}/ab-comparison`)
      setData(res)
      setLoadState('ready')
    } catch {
      setLoadState('error')
    }
  }, [runId])

  useEffect(() => { void load() }, [load])

  const markWinner = useCallback(async (which: 'control' | 'variant') => {
    const side = which === 'control' ? data?.control : data?.variant
    if (!side) return
    setMarking(which)
    setNotice(null)
    try {
      await Promise.all(
        side.steps.map((step) =>
          api.post(`/v1/runs/${encodeURIComponent(side.run_id)}/steps/${encodeURIComponent(step.id)}/feedback`, {
            feedback: 1,
            note: `A/B winner: ${side.model}`,
          }),
        ),
      )
      setNotice({ kind: 'ok', text: `Marked ${which} (${side.model}) as winner — ${side.steps.length} step(s) flagged positive.` })
    } catch {
      setNotice({ kind: 'err', text: `Failed to record feedback for ${which}.` })
    } finally {
      setMarking(null)
    }
  }, [data])

  if (loadState === 'error') {
    return (
      <div className="text-[12px] text-[#f87171] px-4 py-3 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl">
        Failed to load A/B comparison.{' '}
        <button className="underline bg-transparent border-none text-[#f87171] cursor-pointer p-0" onClick={() => void load()}>Retry</button>
      </div>
    )
  }

  if (loadState === 'loading' || !data) {
    return <div className="h-[140px] rounded-xl border border-[rgba(148,163,184,0.1)] bg-[rgba(11,18,32,0.4)] animate-pulse" />
  }

  const controlSteps = data.control?.steps ?? []
  const variantSteps = data.variant?.steps ?? []

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[14px] font-[900] text-[#dce9f7] m-0 tracking-[-0.02em]">A/B Comparison</h3>
        <span className="text-[10px] font-mono text-[#3a5068]">test {data.ab_test_id.slice(0, 8)}…</span>
      </div>

      <div className="flex gap-4 items-start">
        <Column
          label="Control"
          accent="#38bdf8"
          side={data.control}
          steps={controlSteps}
          marking={marking === 'control'}
          onMarkWinner={platform ? () => void markWinner('control') : undefined}
        />
        <Column
          label="Variant"
          accent="#a78bfa"
          side={data.variant}
          steps={variantSteps}
          marking={marking === 'variant'}
          onMarkWinner={platform ? () => void markWinner('variant') : undefined}
        />
      </div>

      {notice && <div className={`text-[11.5px] ${notice.kind === 'err' ? 'text-[#f87171]' : 'text-[#4ade80]'}`}>{notice.text}</div>}
    </div>
  )
}
